import 'yet-another-react-lightbox/styles.css';

import {StyledTooltip} from '@wandb/weave/components/DraggablePopups';
import {LoadingDots} from '@wandb/weave/components/LoadingDots';
import {WeaveflowPeekContext} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/context';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {AutoSizer} from 'react-virtualized';
import Lightbox from 'yet-another-react-lightbox';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';

import {useWFHooks} from '../../pages/wfReactInterface/context';
import {
  ContentMetadataTooltip,
  DownloadButton,
  getIconName,
  IconWithText,
  saveBlob,
} from './Shared';
import {ContentViewMetadataLoadedProps} from './types';

export const HTMLContent = (props: ContentViewMetadataLoadedProps) => (
  <AutoSizer style={{height: '100%', width: '100%'}}>
    {({width, height}) =>
      width === 0 || height === 0 ? null : (
        <TailwindContents>
          <HTMLContentWithSize
            {...props}
            containerWidth={width}
            containerHeight={height}
          />
        </TailwindContents>
      )
    }
  </AutoSizer>
);

type HTMLContentWithSizeProps = ContentViewMetadataLoadedProps & {
  containerWidth: number;
  containerHeight: number;
};

const HTMLContentWithSize: React.FC<HTMLContentWithSizeProps> = ({
  entity,
  project,
  metadata,
  content,
  containerWidth,
  containerHeight,
}) => {
  const [contentResult, setContentResult] = useState<Blob | null>(null);
  const [blobUrl, setBlobUrl] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const {useFileContent} = useWFHooks();
  const {isPeeking} = useContext(WeaveflowPeekContext);
  const {filename, size, mimetype} = metadata;

  const contentContent = useFileContent({
    entity,
    project,
    digest: content,
  });

  // Convert the content to HTML and create blob URL when loaded
  useEffect(() => {
    if (contentContent.result) {
      const blob = new Blob([contentContent.result], {
        type: mimetype,
      });
      setContentResult(blob);

      // Create blob URL for iframe
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setIsDownloading(false);

      // Cleanup function will be handled by component unmount
      return () => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      };
    }
    return;
  }, [contentContent.result, mimetype]);

  const doSave = useCallback(() => {
    if (!contentResult) {
      console.error('No content result');
      return;
    }
    saveBlob(contentResult, filename);
  }, [contentResult, filename]);

  const openPreview = useCallback(() => {
    setShowPreview(true);
    if (!contentResult && !isDownloading) {
      setIsDownloading(true);
    }
  }, [contentResult, isDownloading]);

  const closePreview = useCallback(() => {
    setShowPreview(false);
  }, []);

  const iconName = getIconName(mimetype);

  // Regular icon/link mode for lists and small containers.
  const linkMode = (
    <div className="flex h-full items-center">
      <div className="group flex items-center gap-4">
        <StyledTooltip
          enterDelay={500}
          title={
            <div className="tw-style">
              <ContentMetadataTooltip
                filename={filename}
                mimetype={mimetype}
                size={size}
              />
              <div className="mt-8 flex justify-center text-xs">
                HTML file - click to view
              </div>
            </div>
          }>
          <div>
            <IconWithText
              iconName={iconName}
              filename={filename}
              onClick={openPreview}
            />
          </div>
        </StyledTooltip>
        <div className="opacity-0 transition-opacity group-hover:opacity-100">
          <DownloadButton isDownloading={isDownloading} doSave={doSave} />
        </div>
      </div>
    </div>
  );

  // Full viewer mode - render HTML in lightbox at maximum size
  const lightboxMode = (
    <Lightbox
      open={showPreview}
      plugins={[Fullscreen, Download]}
      close={closePreview}
      controller={{
        closeOnBackdropClick: true,
      }}
      carousel={{finite: true}}
      render={{
        slide: () => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}>
            {blobUrl ? (
              <iframe
                src={blobUrl}
                style={{
                  width: '100vw',
                  height: '100vh',
                  border: 'none',
                }}
                sandbox="allow-scripts" // Do not set allow-same-origin for security - user uploaded content shouldn't access parent origin
                title={`HTML Content: ${filename}`}
              />
            ) : (
              <LoadingDots />
            )}
          </div>
        ),
        buttonPrev: () => null,
        buttonNext: () => null,
      }}
      slides={[{src: blobUrl}]}
    />
  );

  if (isPeeking) {
    if (containerWidth === 0 || containerHeight === 0) return null;
    // If container is below threshold sizes, show IconWithText
    if (containerWidth < 68 || containerHeight < 40) {
      return (
        <>
          {linkMode}
          {lightboxMode}
        </>
      );
    }
    // Container is large enough, show inline HTML render
    return (
      <div className="group relative h-full w-full">
        {blobUrl ? (
          <iframe
            src={blobUrl}
            className="h-full w-full border-0"
            style={{
              width: containerWidth,
              height: containerHeight,
            }}
            sandbox="allow-scripts" // Do not set allow-same-origin for security - user uploaded content shouldn't access parent origin
            title={`HTML Content: ${filename}`}
          />
        ) : (
          <LoadingDots />
        )}
        <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">
          <DownloadButton
            isDownloading={isDownloading}
            doSave={doSave}
            size={'large'}
          />
        </div>
      </div>
    );
  }
  // Return AutoSizer wrapper for isPeeking mode
  if (contentContent.loading) {
    return <LoadingDots />;
  }

  if (!blobUrl) {
    return <span>Loading HTML content...</span>;
  }

  return (
    <>
      {linkMode}
      {lightboxMode}
    </>
  );
};
